import type { HovGainUpgradeOneCostBatchSummary } from "@/features/hoverIdle/types";
import {
  ceilHugeAtLeast,
  compareHuge,
  huge,
  hugeExp10,
  hugeOne,
  type HugeNumber,
} from "@/shared/numbers";

// Caps the number of follow-up purchases a single one-cost bulk purchase call
// can perform. The bulk API also stops on cost != 1, purchase failure, or the
// achievement flag being disabled; this cap is a safety belt against a stuck
// cost=1 plateau freezing the frame.
export const ONE_COST_HOVER_REPEATER_BATCH_MAX_PURCHASES_PER_FIRE = 10000;

export interface OneCostBulkPurchaseCountOptions {
  readonly availableHov: HugeNumber;
  readonly currentRawCost: HugeNumber;
  readonly effectiveCostMultiplier: HugeNumber;
  readonly rawCostMultiplier: number;
  readonly cap?: number;
}

export interface OneCostBulkPurchaseSummaryOptions extends OneCostBulkPurchaseCountOptions {
  readonly batchEnabled: boolean;
  readonly currentEnhancementLevel: number;
  readonly effectiveCost: HugeNumber;
  readonly upgradeAvailable?: boolean;
}

interface OneCostPlateauCountOptions {
  readonly currentRawCost: HugeNumber;
  readonly costThreshold: HugeNumber;
  readonly rawCostMultiplier: number;
  readonly cap: number;
}

type OneCostBulkPurchaseCountResolver = (
  options: OneCostBulkPurchaseCountOptions,
) => number;

export function createOneCostBulkPurchaseLightweightSummary(
  options: OneCostBulkPurchaseSummaryOptions,
): HovGainUpgradeOneCostBatchSummary | undefined {
  return createOneCostBulkPurchaseSummaryWithCount(
    options,
    resolveOneCostBulkPurchaseLightweightCount,
  );
}

export function createOneCostBulkPurchaseExactSummary(
  options: OneCostBulkPurchaseSummaryOptions,
): HovGainUpgradeOneCostBatchSummary | undefined {
  return createOneCostBulkPurchaseSummaryWithCount(
    options,
    resolveOneCostBulkPurchaseExactCount,
  );
}

export function resolveOneCostBulkPurchaseLightweightCount(
  options: OneCostBulkPurchaseCountOptions,
): number {
  const cap = resolveOneCostBulkPurchaseCap(options.cap);
  if (cap <= 0) return 0;
  if (compareHuge(options.effectiveCostMultiplier, 0) <= 0) {
    return 0;
  }
  const affordableCount = resolveAffordableOneCostPurchaseCount(
    options.availableHov,
    cap,
  );
  if (affordableCount <= 0) {
    return 0;
  }

  return resolveOneCostPlateauLightweightPurchaseCount({
    currentRawCost: options.currentRawCost,
    costThreshold: hugeOne().div(options.effectiveCostMultiplier),
    rawCostMultiplier: options.rawCostMultiplier,
    cap: affordableCount,
  });
}

export function resolveOneCostBulkPurchaseExactCount(
  options: OneCostBulkPurchaseCountOptions,
): number {
  const cap = resolveOneCostBulkPurchaseCap(options.cap);
  if (cap <= 0) return 0;
  if (compareHuge(options.effectiveCostMultiplier, 0) <= 0) {
    return 0;
  }
  const affordableCount = resolveAffordableOneCostPurchaseCount(
    options.availableHov,
    cap,
  );
  if (affordableCount <= 0) {
    return 0;
  }

  return resolveOneCostPlateauPurchaseCount({
    currentRawCost: options.currentRawCost,
    costThreshold: hugeOne().div(options.effectiveCostMultiplier),
    rawCostMultiplier: options.rawCostMultiplier,
    cap: affordableCount,
  });
}

function createOneCostBulkPurchaseSummaryWithCount(
  options: OneCostBulkPurchaseSummaryOptions,
  resolvePurchaseCount: OneCostBulkPurchaseCountResolver,
): HovGainUpgradeOneCostBatchSummary | undefined {
  if (
    options.upgradeAvailable === false ||
    !isOneCostBulkPurchasePreviewActive({
      batchEnabled: options.batchEnabled,
      effectiveCost: options.effectiveCost,
    }) ||
    compareHuge(options.availableHov, options.effectiveCost) < 0
  ) {
    return undefined;
  }

  const purchaseCount = resolvePurchaseCount(options);
  if (purchaseCount <= 0) {
    return undefined;
  }

  return {
    purchaseCount,
    enhancementLevelAfter: options.currentEnhancementLevel + purchaseCount,
  };
}

function isOneCostBulkPurchasePreviewActive(options: {
  readonly batchEnabled: boolean;
  readonly effectiveCost: HugeNumber;
}): boolean {
  return options.batchEnabled && compareHuge(options.effectiveCost, 1) === 0;
}

function resolveOneCostBulkPurchaseCap(cap: number | undefined): number {
  return Math.max(
    0,
    Math.floor(cap ?? ONE_COST_HOVER_REPEATER_BATCH_MAX_PURCHASES_PER_FIRE),
  );
}

function resolveAffordableOneCostPurchaseCount(
  availableHov: HugeNumber,
  cap: number,
): number {
  if (compareHuge(availableHov, 1) < 0) {
    return 0;
  }
  if (compareHuge(availableHov, cap) >= 0) {
    return cap;
  }
  return Math.max(0, Math.min(cap, Math.floor(availableHov.toNumber())));
}

function resolveOneCostPlateauLightweightPurchaseCount(
  options: OneCostPlateauCountOptions,
): number {
  const cap = Math.max(0, Math.floor(options.cap));
  if (
    cap <= 0 ||
    compareHuge(options.currentRawCost, options.costThreshold) > 0
  ) {
    return 0;
  }
  if (cap === 1) {
    return 1;
  }
  if (
    !Number.isFinite(options.rawCostMultiplier) ||
    options.rawCostMultiplier <= 0
  ) {
    return 1;
  }
  if (options.rawCostMultiplier <= 1) {
    return cap;
  }

  // Upper-bound ceil growth so the button count stays conservative without
  // simulating every plateau purchase during snapshot/render.
  const roundingAllowanceBase = 1 / (options.rawCostMultiplier - 1);
  const ratio = options.costThreshold
    .add(roundingAllowanceBase)
    .div(options.currentRawCost.add(roundingAllowanceBase));
  if (compareHuge(ratio, 1) <= 0) {
    return 1;
  }

  const logRatio = ratio.log10().toNumber();
  const logMultiplier = Math.log10(options.rawCostMultiplier);
  if (!Number.isFinite(logMultiplier) || logMultiplier <= 0) {
    return 1;
  }
  if (!Number.isFinite(logRatio)) {
    return cap;
  }

  const maxOffset = Math.floor(logRatio / logMultiplier - 1e-12);
  return Math.max(1, Math.min(cap, maxOffset + 1));
}

function resolveOneCostPlateauPurchaseCount(
  options: OneCostPlateauCountOptions,
): number {
  const cap = Math.max(0, Math.floor(options.cap));
  if (
    cap <= 0 ||
    compareHuge(options.currentRawCost, options.costThreshold) > 0
  ) {
    return 0;
  }
  if (options.rawCostMultiplier <= 1) {
    return cap;
  }

  const numericCount = resolveOneCostPlateauPurchaseCountWithNumbers({
    currentRawCost: options.currentRawCost,
    costThreshold: options.costThreshold,
    rawCostMultiplier: options.rawCostMultiplier,
    cap,
  });
  if (numericCount !== null) {
    return numericCount;
  }

  if (
    isOneCostPlateauGuaranteedThroughCap({
      currentRawCost: options.currentRawCost,
      costThreshold: options.costThreshold,
      rawCostMultiplier: options.rawCostMultiplier,
      cap,
    })
  ) {
    return cap;
  }

  return resolveOneCostPlateauPurchaseCountWithHugeSolver({
    currentRawCost: options.currentRawCost,
    costThreshold: options.costThreshold,
    rawCostMultiplier: options.rawCostMultiplier,
    cap,
  });
}

// break_eternity promotes a layer-0 magnitude at or above this bound (its
// EXP_LIMIT) to layer 1, storing log10 of the value. The huge-domain solver
// below mirrors that boundary and relies on layer-1 mul(number) being a single
// double add of Math.log10(multiplier); resolveOneCostPlateauLayer1Count
// re-validates that model against a real HugeNumber step at every magnitude
// bin entry and falls back to the verbatim walk on any mismatch.
const HUGE_LAYER0_PROMOTION_LIMIT = 9e15;

const DOUBLE_MANTISSA_LOW = 2 ** 52;
const DOUBLE_MANTISSA_HIGH = 2 ** 53;

// Largest integer rawCost for which one plateau step is provably an exact +1
// in double arithmetic: for integer r >= 1 and 1 < multiplier < 2 with
// r * (multiplier - 1) <= 1 in real arithmetic, ceil(r * multiplier) is
// exactly r + 1 (the rounded product stays in (r, r + 1]). multiplier - 1 is
// exact for multipliers in (1, 2), and 1 / (multiplier - 1) carries at most
// 0.5 absolute rounding error, so the extra -1 keeps the returned limit at or
// below the true regime end. Returns 0 when no jump regime is provable.
function resolveOneCostPlateauUnitStepLimit(rawCostMultiplier: number): number {
  if (!(rawCostMultiplier > 1 && rawCostMultiplier < 2)) {
    return 0;
  }
  return Math.floor(1 / (rawCostMultiplier - 1)) - 1;
}

function resolveOneCostPlateauPurchaseCountWithNumbers(
  options: OneCostPlateauCountOptions,
): number | null {
  let rawCost = options.currentRawCost.toNumber();
  const threshold = options.costThreshold.toNumber();
  if (
    !Number.isFinite(rawCost) ||
    !Number.isFinite(threshold) ||
    rawCost > Number.MAX_SAFE_INTEGER ||
    threshold > Number.MAX_SAFE_INTEGER
  ) {
    return null;
  }
  const unitStepLimit = resolveOneCostPlateauUnitStepLimit(
    options.rawCostMultiplier,
  );
  const thresholdFloor = Math.floor(threshold);
  let count = 1;
  while (count < options.cap) {
    if (rawCost >= 1 && rawCost <= unitStepLimit && Number.isInteger(rawCost)) {
      const jump = Math.min(
        unitStepLimit - rawCost + 1,
        thresholdFloor - rawCost,
        options.cap - count,
      );
      if (jump >= 1) {
        rawCost += jump;
        count += jump;
        continue;
      }
    }
    rawCost = Math.max(1, Math.ceil(rawCost * options.rawCostMultiplier));
    if (rawCost > threshold) break;
    count += 1;
  }
  return count;
}

function isOneCostPlateauGuaranteedThroughCap(
  options: OneCostPlateauCountOptions,
): boolean {
  if (options.cap <= 1) {
    return true;
  }
  const offset = options.cap - 1;
  const multiplier = huge(options.rawCostMultiplier);
  const growth = multiplier.pow(offset);
  const roundingAllowance = growth.sub(1).div(options.rawCostMultiplier - 1);
  const rawCostUpperBound = options.currentRawCost
    .mul(growth)
    .add(roundingAllowance);
  return compareHuge(rawCostUpperBound, options.costThreshold) <= 0;
}

interface OneCostPlateauHugeWalkState {
  rawCost: HugeNumber;
  count: number;
}

interface OneCostPlateauLayer1Jump {
  readonly mag: number;
  readonly stepsTaken: number;
}

export interface OneCostPlateauRawCostAdvance {
  readonly rawCost: HugeNumber;
  readonly stepsTaken: number;
}

function resolveOneCostPlateauPurchaseCountWithHugeSolver(
  options: OneCostPlateauCountOptions,
): number {
  if (!isOneCostPlateauHugeFastPathInput(options)) {
    return walkOneCostPlateauHugeLoop(options, options.currentRawCost, 1);
  }
  const state: OneCostPlateauHugeWalkState = {
    rawCost: options.currentRawCost,
    count: 1,
  };
  if (state.rawCost.layer === 0) {
    const segment = advanceOneCostPlateauLayer0Segment(
      options.rawCostMultiplier,
      state.rawCost,
      options.cap - state.count,
    );
    state.rawCost = segment.rawCost;
    state.count += segment.stepsTaken;
    if (state.count >= options.cap) {
      return state.count;
    }
  }
  return resolveOneCostPlateauLayer1Count(options, state);
}

// The huge-domain fast path assumes a normalized non-negative rawCost at layer
// 0 or 1 and a layer>=1 threshold (the numeric-domain path already resolves
// every layer-0 threshold that can be crossed). Anything else takes the
// verbatim walk.
function isOneCostPlateauHugeFastPathInput(
  options: OneCostPlateauCountOptions,
): boolean {
  const rawCost = options.currentRawCost;
  const threshold = options.costThreshold;
  return (
    Number.isFinite(options.rawCostMultiplier) &&
    options.rawCostMultiplier > 1 &&
    options.rawCostMultiplier < HUGE_LAYER0_PROMOTION_LIMIT &&
    rawCost.sign >= 0 &&
    (rawCost.layer === 0 || rawCost.layer === 1) &&
    Number.isFinite(rawCost.mag) &&
    threshold.sign === 1 &&
    threshold.layer >= 1 &&
    Number.isFinite(threshold.layer) &&
    Number.isFinite(threshold.mag)
  );
}

// Layer-0 stretch of the huge-domain walks in plain doubles. break_eternity
// performs the same double ops at layer 0 (one rounded product, then
// Math.ceil), and a normalized layer-0 rawCost always compares below the
// layer>=1 thresholds/stops the fast paths admit, so no step in this segment
// can end the walk early. The segment ends at the step budget or just before
// the step whose result reaches the layer-promotion bound; that pending step
// is left to the layer-1 loop so normalization and the exact compares stay
// verbatim.
function advanceOneCostPlateauLayer0Segment(
  multiplier: number,
  initialRawCost: HugeNumber,
  stepBudget: number,
): OneCostPlateauRawCostAdvance {
  const unitStepLimit = resolveOneCostPlateauUnitStepLimit(multiplier);
  let rawCost = initialRawCost.sign === 0 ? 0 : initialRawCost.mag;
  let steps = 0;

  while (steps < stepBudget) {
    if (rawCost >= 1 && rawCost <= unitStepLimit && Number.isInteger(rawCost)) {
      const jump = Math.min(unitStepLimit - rawCost + 1, stepBudget - steps);
      if (jump >= 1) {
        rawCost += jump;
        steps += jump;
        continue;
      }
    }
    const product = rawCost * multiplier;
    const stepped =
      product >= HUGE_LAYER0_PROMOTION_LIMIT
        ? product
        : Math.max(1, Math.ceil(product));
    if (stepped >= HUGE_LAYER0_PROMOTION_LIMIT) {
      break;
    }
    rawCost = stepped;
    steps += 1;
  }

  return { rawCost: huge(rawCost), stepsTaken: steps };
}

// Layer-1 stretch. break_eternity multiplies a layer-1 value by a plain number
// as one double add of Math.log10(multiplier) on `mag`. Each iteration takes
// one verbatim HugeNumber step (which owns the threshold compare and any layer
// transition), checks that step against the add model, and then advances
// through the rest of the current magnitude bin in closed form via
// jumpOneCostPlateauLayer1Bin. A model mismatch hands the remaining walk to
// the verbatim loop unchanged.
function resolveOneCostPlateauLayer1Count(
  options: OneCostPlateauCountOptions,
  state: OneCostPlateauHugeWalkState,
): number {
  const log10Multiplier = Math.log10(options.rawCostMultiplier);
  let rawCost = state.rawCost;
  let count = state.count;

  while (count < options.cap) {
    const expectedMag =
      rawCost.layer === 1 ? rawCost.mag + log10Multiplier : Number.NaN;
    rawCost = ceilHugeAtLeast(rawCost.mul(options.rawCostMultiplier), 1);
    if (compareHuge(rawCost, options.costThreshold) > 0) {
      return count;
    }
    count += 1;
    if (count >= options.cap) {
      return count;
    }
    if (
      rawCost.layer !== 1 ||
      rawCost.sign !== 1 ||
      !Number.isFinite(rawCost.mag) ||
      (!Number.isNaN(expectedMag) && rawCost.mag !== expectedMag)
    ) {
      return walkOneCostPlateauHugeLoop(options, rawCost, count);
    }

    const quantum = resolveLayer1BinQuantum(rawCost.mag);
    const threshold = options.costThreshold;
    const thresholdBoundMantissa =
      threshold.layer === 1 && threshold.mag < DOUBLE_MANTISSA_HIGH * quantum
        ? threshold.mag / quantum
        : null;
    const jump = jumpOneCostPlateauLayer1Bin(
      rawCost.mag,
      quantum,
      options.cap - count,
      log10Multiplier,
      thresholdBoundMantissa,
    );
    if (jump === null) {
      // Stalled below the threshold: the magnitude can never change again, so
      // every remaining step just counts up to the cap.
      return options.cap;
    }
    if (jump.stepsTaken > 0) {
      rawCost = hugeExp10(jump.mag);
      count += jump.stepsTaken;
    }
  }
  return count;
}

// Closed-form advance through one double-exponent bin of the layer-1
// magnitude. Within a bin every magnitude sits on the same ulp grid
// (mag = mantissa * quantum with an integer mantissa), the exact step addend
// is the constant scaled = log10Multiplier / quantum, and IEEE
// round-to-nearest turns each step into the same integer mantissa increment
// (ties round to the even mantissa and stabilize after at most one step).
// Jumps stop at the bin top, the layer-promotion bound, the caller's mantissa
// bound (threshold or stop, resolved by the caller against the same quantum),
// and the step budget; the boundary-crossing step itself is always taken
// verbatim by the caller. Returns null when the increment is zero: the walk
// is stalled and the magnitude cannot change while it stays in this bin.
function jumpOneCostPlateauLayer1Bin(
  mag: number,
  quantum: number,
  stepBudget: number,
  log10Multiplier: number,
  boundMantissa: number | null,
): OneCostPlateauLayer1Jump | null {
  let mantissa = mag / quantum;
  const scaled = log10Multiplier / quantum;
  const base = Math.floor(scaled);
  const fraction = scaled - base;

  let maxMantissa = DOUBLE_MANTISSA_HIGH - 1;
  const promotionMantissa =
    Math.floor(HUGE_LAYER0_PROMOTION_LIMIT / quantum) - 1;
  if (promotionMantissa < maxMantissa) {
    maxMantissa = promotionMantissa;
  }
  if (boundMantissa !== null && boundMantissa < maxMantissa) {
    maxMantissa = boundMantissa;
  }

  let stepsTaken = 0;
  let stepIncrement: number;
  if (fraction > 0.5) {
    stepIncrement = base + 1;
  } else if (fraction < 0.5) {
    stepIncrement = base;
  } else {
    const firstIncrement = mantissa % 2 === base % 2 ? base : base + 1;
    if (firstIncrement === 0) {
      return null;
    }
    if (mantissa + firstIncrement > maxMantissa || stepBudget <= 0) {
      return { mag, stepsTaken: 0 };
    }
    mantissa += firstIncrement;
    stepsTaken = 1;
    stepIncrement = base % 2 === 0 ? base : base + 1;
  }
  if (stepIncrement === 0) {
    return null;
  }

  let jumpSteps = Math.min(
    Math.floor((maxMantissa - mantissa) / stepIncrement),
    stepBudget - stepsTaken,
  );
  while (jumpSteps > 0 && mantissa + jumpSteps * stepIncrement > maxMantissa) {
    jumpSteps -= 1;
  }
  if (jumpSteps > 0) {
    mantissa += jumpSteps * stepIncrement;
    stepsTaken += jumpSteps;
  }
  return { mag: mantissa * quantum, stepsTaken };
}

function resolveLayer1BinQuantum(mag: number): number {
  let quantum = 2 ** (Math.floor(Math.log2(mag)) - 52);
  const mantissa = mag / quantum;
  if (mantissa >= DOUBLE_MANTISSA_HIGH) {
    quantum *= 2;
  } else if (mantissa < DOUBLE_MANTISSA_LOW) {
    quantum /= 2;
  }
  return quantum;
}

// Raw cost after up to `maxSteps` plateau steps (each step is
// `ceilHugeAtLeast(rawCost.mul(rawCostMultiplier), 1)`), never stepping from a
// rawCost that compares at or above `stopAtOrAbove`. Byte-identical to running
// the verbatim per-step loop; the fast path reuses the purchase-count solver's
// layer-0 double segment and layer-1 mantissa-bin jumps and falls back to the
// verbatim loop on any model mismatch.
export function advanceOneCostPlateauRawCost(
  rawCost: HugeNumber,
  rawCostMultiplier: number,
  maxSteps: number,
  stopAtOrAbove: HugeNumber | null,
): OneCostPlateauRawCostAdvance {
  const stepBudget = Math.max(0, Math.floor(maxSteps));
  if (stepBudget <= 0) {
    return { rawCost, stepsTaken: 0 };
  }
  if (
    !isOneCostPlateauAdvanceFastPathInput(
      rawCost,
      rawCostMultiplier,
      stopAtOrAbove,
    )
  ) {
    return walkOneCostPlateauAdvanceLoop(
      rawCostMultiplier,
      stopAtOrAbove,
      rawCost,
      stepBudget,
    );
  }
  let current = rawCost;
  let stepsTaken = 0;
  if (current.layer === 0) {
    const segment = advanceOneCostPlateauLayer0Segment(
      rawCostMultiplier,
      current,
      stepBudget,
    );
    current = segment.rawCost;
    stepsTaken = segment.stepsTaken;
    if (stepsTaken >= stepBudget) {
      return { rawCost: current, stepsTaken };
    }
  }
  const layer1 = advanceOneCostPlateauLayer1Steps(
    rawCostMultiplier,
    stopAtOrAbove,
    current,
    stepBudget - stepsTaken,
  );
  return {
    rawCost: layer1.rawCost,
    stepsTaken: stepsTaken + layer1.stepsTaken,
  };
}

// The advance fast path assumes a normalized non-negative rawCost at layer 0
// or 1 and a stop bound no layer-0 value can reach (at or above the
// layer-promotion limit), so the layer-0 segment can skip the stop compare.
// Anything else takes the verbatim walk.
function isOneCostPlateauAdvanceFastPathInput(
  rawCost: HugeNumber,
  rawCostMultiplier: number,
  stopAtOrAbove: HugeNumber | null,
): boolean {
  return (
    Number.isFinite(rawCostMultiplier) &&
    rawCostMultiplier > 1 &&
    rawCostMultiplier < HUGE_LAYER0_PROMOTION_LIMIT &&
    rawCost.sign >= 0 &&
    (rawCost.layer === 0 || rawCost.layer === 1) &&
    Number.isFinite(rawCost.mag) &&
    (stopAtOrAbove === null ||
      (stopAtOrAbove.sign === 1 &&
        Number.isFinite(stopAtOrAbove.layer) &&
        Number.isFinite(stopAtOrAbove.mag) &&
        compareHuge(stopAtOrAbove, HUGE_LAYER0_PROMOTION_LIMIT) >= 0))
  );
}

// Layer-1 stretch of the raw-cost advance. Mirrors
// resolveOneCostPlateauLayer1Count's structure: one verbatim HugeNumber step
// per magnitude bin entry (preceded by the exact stop compare and validated
// against the layer-1 double-add model), then a closed-form jump through the
// rest of the bin. Jump destinations stay strictly below the stop magnitude,
// so the stop-crossing step is always compared and taken verbatim. A model
// mismatch hands the remaining steps to the verbatim loop unchanged.
function advanceOneCostPlateauLayer1Steps(
  rawCostMultiplier: number,
  stopAtOrAbove: HugeNumber | null,
  initialRawCost: HugeNumber,
  stepBudget: number,
): OneCostPlateauRawCostAdvance {
  const log10Multiplier = Math.log10(rawCostMultiplier);
  let rawCost = initialRawCost;
  let steps = 0;

  while (steps < stepBudget) {
    if (stopAtOrAbove !== null && compareHuge(rawCost, stopAtOrAbove) >= 0) {
      break;
    }
    const expectedMag =
      rawCost.layer === 1 ? rawCost.mag + log10Multiplier : Number.NaN;
    rawCost = ceilHugeAtLeast(rawCost.mul(rawCostMultiplier), 1);
    steps += 1;
    if (steps >= stepBudget) {
      break;
    }
    if (
      rawCost.layer !== 1 ||
      rawCost.sign !== 1 ||
      !Number.isFinite(rawCost.mag) ||
      (!Number.isNaN(expectedMag) && rawCost.mag !== expectedMag)
    ) {
      const walked = walkOneCostPlateauAdvanceLoop(
        rawCostMultiplier,
        stopAtOrAbove,
        rawCost,
        stepBudget - steps,
      );
      return { rawCost: walked.rawCost, stepsTaken: steps + walked.stepsTaken };
    }

    const quantum = resolveLayer1BinQuantum(rawCost.mag);
    const stopBoundMantissa =
      stopAtOrAbove !== null &&
      stopAtOrAbove.layer === 1 &&
      stopAtOrAbove.mag < DOUBLE_MANTISSA_HIGH * quantum
        ? Math.ceil(stopAtOrAbove.mag / quantum) - 1
        : null;
    const jump = jumpOneCostPlateauLayer1Bin(
      rawCost.mag,
      quantum,
      stepBudget - steps,
      log10Multiplier,
      stopBoundMantissa,
    );
    if (jump === null) {
      // Stalled bin (zero mantissa increment). One probe step proves the fixed
      // point: a step that reproduces its own input components must keep
      // reproducing it, so all remaining steps collapse into the probe result.
      if (stopAtOrAbove !== null && compareHuge(rawCost, stopAtOrAbove) >= 0) {
        break;
      }
      const probed = ceilHugeAtLeast(rawCost.mul(rawCostMultiplier), 1);
      steps += 1;
      if (
        probed.layer === 1 &&
        probed.sign === 1 &&
        probed.mag === rawCost.mag
      ) {
        return { rawCost: probed, stepsTaken: stepBudget };
      }
      rawCost = probed;
      if (steps >= stepBudget) {
        break;
      }
      const walked = walkOneCostPlateauAdvanceLoop(
        rawCostMultiplier,
        stopAtOrAbove,
        rawCost,
        stepBudget - steps,
      );
      return { rawCost: walked.rawCost, stepsTaken: steps + walked.stepsTaken };
    }
    if (jump.stepsTaken > 0) {
      rawCost = hugeExp10(jump.mag);
      steps += jump.stepsTaken;
    }
  }
  return { rawCost, stepsTaken: steps };
}

// Verbatim plateau raw-cost advance, kept as the fallback path and as the
// reference semantics for advanceOneCostPlateauRawCost: step up to `maxSteps`
// times, checking the stop bound before each step.
function walkOneCostPlateauAdvanceLoop(
  rawCostMultiplier: number,
  stopAtOrAbove: HugeNumber | null,
  initialRawCost: HugeNumber,
  maxSteps: number,
): OneCostPlateauRawCostAdvance {
  let rawCost = initialRawCost;
  let steps = 0;
  while (
    steps < maxSteps &&
    (stopAtOrAbove === null || compareHuge(rawCost, stopAtOrAbove) < 0)
  ) {
    rawCost = ceilHugeAtLeast(rawCost.mul(rawCostMultiplier), 1);
    steps += 1;
  }
  return { rawCost, stepsTaken: steps };
}

// Verbatim HugeNumber plateau walk, kept as the huge-domain fallback path.
// initialRawCost / initialCount let the fast solver hand over mid-walk without
// changing the step sequence.
function walkOneCostPlateauHugeLoop(
  options: OneCostPlateauCountOptions,
  initialRawCost: HugeNumber,
  initialCount: number,
): number {
  let rawCost = initialRawCost;
  let count = initialCount;
  while (count < options.cap) {
    rawCost = ceilHugeAtLeast(rawCost.mul(options.rawCostMultiplier), 1);
    if (compareHuge(rawCost, options.costThreshold) > 0) break;
    count += 1;
  }
  return count;
}
